"use client";

import { useRef, type ElementType, type HTMLAttributes } from "react";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";

type AnimatedCardProps = HTMLAttributes<HTMLElement> & {
  as?: ElementType;
  /** Position in its row — drives the stagger delay (or the cascade offset when scrubbed). */
  index?: number;
  stagger?: number;
  y?: number;
  x?: number;
  scale?: number;
  duration?: number;
  delay?: number;
  start?: string;
  /**
   * Scroll-linked entrance (desktop). Combined with `cascade`, later cards start further
   * down and catch up as you scroll — the diagonal "wave" the reference uses for rows.
   * Phones fall back to a simple one-shot reveal per card.
   */
  scrub?: boolean | number;
  cascade?: number;
  /** Which element's position fires the reveal. "parent" keeps a row in sync. */
  trigger?: "self" | "parent";
};

export function AnimatedCard({
  as: Tag = "div",
  index = 0,
  stagger = 0.1,
  y = 50,
  x = 0,
  scale = 0.98,
  duration = 1.25,
  delay = 0,
  start = "top 84%",
  scrub,
  cascade = 0,
  trigger = "parent",
  className,
  children,
  ...rest
}: AnimatedCardProps) {
  const ref = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      if (reduceMotion()) {
        gsap.set(el, { opacity: 1 });
        return;
      }
      const parent = el.parentElement ?? el;

      const oneShot = (triggerEl: Element, offset: number, wait: number) =>
        gsap.fromTo(
          el,
          { opacity: 0, y: offset, x, scale },
          {
            opacity: 1,
            y: 0,
            x: 0,
            scale: 1,
            duration,
            ease: "silk",
            delay: wait,
            scrollTrigger: { trigger: triggerEl, start, toggleActions: PLAY_ONCE },
          },
        );

      if (!scrub) {
        oneShot(trigger === "parent" ? parent : el, y + index * cascade, delay + index * stagger);
        return;
      }

      const mm = gsap.matchMedia();
      mm.add("(min-width: 768px)", () => {
        gsap
          .timeline({
            scrollTrigger: {
              trigger: trigger === "parent" ? parent : el,
              start: "top bottom",
              end: "top 40%",
              scrub: scrub === true ? 0.9 : scrub,
            },
          })
          .fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.35, ease: "none" }, 0)
          .fromTo(
            el,
            { y: y + index * cascade, x, scale },
            { y: 0, x: 0, scale: 1, duration: 1, ease: "drift" },
            0,
          );
      });
      mm.add("(max-width: 767px)", () => {
        oneShot(el, Math.min(y, 60), 0);
      });
    },
    { scope: ref },
  );

  return (
    <Tag ref={ref} data-reveal-init="" className={cn(className)} {...rest}>
      {children}
    </Tag>
  );
}
